/**
 * Stream B — B33: Review Intelligence Dashboard
 *
 * Review metrics, platform breakdown, rating distribution.
 * Real apiFetch to /crm/reviews/intelligence. No mocks. CSS variables only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Star, MessageCircle, Clock } from "lucide-react";

/** Matches the shape returned by GET /crm/reviews/intelligence */
interface ReviewMetrics {
  averageRating: number;
  totalReviews: number;
  responseRate: number;
  avgResponseTimeHours: number;
  industryAvgResponseRate: number | null;
  sentimentTrends: Array<{ month: string; positive: number; neutral: number; negative: number }>;
  platforms: Array<{ name: string; rating: number; count: number; responseRate: number }>;
  ratingDistribution: Record<number, number>;
  aiDrafts: Array<{
    reviewId: string;
    reviewerName: string;
    platform: string;
    rating: number;
    reviewText: string;
    draftResponse: string;
    sentiment: "positive" | "neutral" | "negative";
    createdAt: string;
  }>;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i <= Math.round(rating) ? "fill-[var(--color-warning)] text-[var(--color-warning)]" : "text-[var(--color-border)]"}`}
        />
      ))}
    </div>
  );
}

export default function ReviewDashboard() {
  const [metrics, setMetrics] = useState<ReviewMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: ReviewMetrics }>("/crm/reviews/intelligence");
      setMetrics(res.data);
    } catch {
      setMetrics(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-[var(--color-surface)]" />
        ))}
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
        <Star className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
        <p className="font-medium text-[var(--color-foreground)]">No review data yet</p>
        <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Connect a Google Business Profile or Facebook Page to start collecting review intelligence.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Review Intelligence</h1>

      {/* Top metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="text-sm text-[var(--color-muted-foreground)]">Average Rating</div>
          <div className="mt-1 text-3xl font-bold text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
            {metrics.averageRating.toFixed(1)}
          </div>
          <StarRating rating={metrics.averageRating} />
          <div className="text-xs text-[var(--color-muted-foreground)]">{metrics.totalReviews} total reviews</div>
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center gap-1 text-sm text-[var(--color-muted-foreground)]">
            <MessageCircle className="h-3.5 w-3.5" /> Response Rate
          </div>
          <div className="mt-1 text-3xl font-bold text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
            {metrics.responseRate}%
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)]">
            {metrics.industryAvgResponseRate !== null ? `Industry avg: ${metrics.industryAvgResponseRate}%` : "No industry benchmark yet"}
          </div>
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center gap-1 text-sm text-[var(--color-muted-foreground)]">
            <Clock className="h-3.5 w-3.5" /> Avg Response Time
          </div>
          <div className="mt-1 text-3xl font-bold text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
            {metrics.avgResponseTimeHours > 0 ? `${metrics.avgResponseTimeHours}h` : "--"}
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)]">Hours to first response</div>
        </div>
      </div>

      {/* Platform breakdown */}
      {metrics.platforms.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3">Platform Breakdown</h2>
          <div className="space-y-3">
            {metrics.platforms.map((p) => (
              <div key={p.name} className="flex items-center justify-between">
                <span className="font-medium text-[var(--color-foreground)]">{p.name}</span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <StarRating rating={p.rating} />
                    <span className="text-sm text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {p.rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-sm text-[var(--color-muted-foreground)]">{p.count} reviews</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rating distribution */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
        <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3">Rating Distribution</h2>
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = metrics.ratingDistribution[star] ?? 0;
            const pct = metrics.totalReviews > 0 ? (count / metrics.totalReviews) * 100 : 0;
            const barColor = star >= 4 ? "var(--color-success)" : star === 3 ? "var(--color-warning)" : "var(--color-error)";
            return (
              <div key={star} className="flex items-center gap-3">
                <span className="w-8 text-sm text-[var(--color-muted-foreground)]">{star}</span>
                <Star className="h-3.5 w-3.5 text-[var(--color-warning)]" />
                <div className="flex-1 h-3 rounded-full bg-[var(--color-surface)] overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                </div>
                <span className="w-12 text-right text-xs text-[var(--color-muted-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sentiment trends */}
      {metrics.sentimentTrends.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3">Sentiment Trends (6 months)</h2>
          <div className="grid grid-cols-6 gap-2">
            {metrics.sentimentTrends.map((bucket) => {
              const total = bucket.positive + bucket.neutral + bucket.negative;
              return (
                <div key={bucket.month} className="text-center">
                  <div className="text-xs font-medium text-[var(--color-muted-foreground)] mb-1">{bucket.month}</div>
                  {total > 0 ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-xs text-[var(--color-success)]">{bucket.positive}</span>
                      <span className="text-xs text-[var(--color-muted-foreground)]">{bucket.neutral}</span>
                      <span className="text-xs text-[var(--color-error)]">{bucket.negative}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-[var(--color-muted-foreground)]">--</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs text-[var(--color-muted-foreground)]">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[var(--color-success)]" /> Positive</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[var(--color-muted-foreground)]" /> Neutral</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[var(--color-error)]" /> Negative</span>
          </div>
        </div>
      )}

      {/* AI draft responses */}
      {metrics.aiDrafts.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3">AI Draft Responses</h2>
          <div className="space-y-3">
            {metrics.aiDrafts.map((draft) => (
              <div key={draft.reviewId} className="rounded-lg border border-[var(--color-border)] p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">{draft.reviewerName}</span>
                  <div className="flex items-center gap-2">
                    <StarRating rating={draft.rating} />
                    <span className="text-xs text-[var(--color-muted-foreground)]">{draft.platform}</span>
                  </div>
                </div>
                {draft.reviewText && (
                  <p className="text-sm text-[var(--color-muted-foreground)] mb-2 line-clamp-2">{draft.reviewText}</p>
                )}
                <div className="rounded-md bg-[var(--color-surface)] p-2">
                  <p className="text-xs text-[var(--color-muted-foreground)] mb-0.5">AI draft:</p>
                  <p className="text-sm text-[var(--color-foreground)] line-clamp-3">{draft.draftResponse}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
